declare const BASE_API_URL: string;
declare const STOREFRONT_BIG_COMMERCE_API_URL: string;

export enum HTTP_METHOD {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
  OPTIONS = "OPTIONS",
  HEAD = "HEAD",
}

const getToken = async () => {
  // retrieve token from local or jarjar
  let localToken = localStorage.getItem("bigCommerceStorefrontToken");
  if (localToken) {
    return localToken;
  }
  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  let response = await fetch(
    `${BASE_API_URL}/v1/service/bigcommerce/storefront-auth/eve`,
    { headers, method: HTTP_METHOD.GET }
  );
  let respJson = await handleErrors(response);
  if (respJson.token) {
    localStorage.setItem("bigCommerceStorefrontToken", respJson.token);
    return respJson.token;
  } else {
    return respJson;
  }
};

const fetchAuthedHeaders = async (headers: Headers = new Headers()) => {
  const accessToken = await getToken();
  headers.append("Authorization", `Bearer ${accessToken}`);
  headers.append("Content-Type", "application/json");
  return headers;
};

const handleErrors = (response: Response): Promise<any> => {
  if (response.status === 500) {
    throw Error(response.statusText);
  }
  if (response.status === 401) {
    return Promise.reject({
      message: response.status.toString(),
      error: response.status,
    });
  }
  if (response.status === 404) {
    return Promise.reject({
      message: response.status.toString(),
      error: response.status,
    });
  }
  if (response.status === 204) {
    return Promise.resolve({});
  }
  if (!response.ok) {
    return response.json().then((json) => Promise.reject(JSON.stringify(json)));
  } else {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/pdf")) {
      return response.blob();
    }
    return response.json();
  }
};

const recursiveAttemptRequest = async (
  endPoint: string,
  method: HTTP_METHOD = HTTP_METHOD.GET,
  headers: HeadersInit = {},
  content: any = undefined,
  count: number = 0
): Promise<any> => {
  let authedHeaders = new Headers(headers);
  let response;

  try {
    authedHeaders = await fetchAuthedHeaders(new Headers(headers));
  } catch (err) {
    handleErrors(err);
  }

  try {
    response = await fetch(`${STOREFRONT_BIG_COMMERCE_API_URL}`, {
      headers: authedHeaders,
      method,
      credentials: "include",
      mode: "cors",
      body: JSON.stringify(content),
    });
  } catch (err) {
    const error = await handleBigCommerceError(count);
    if (error) {
      return error;
    } else {
      return recursiveAttemptRequest(
        `${STOREFRONT_BIG_COMMERCE_API_URL}`,
        method,
        headers,
        content,
        ++count
      );
    }
  }
  if (response?.status === 401) {
    const error = await handleBigCommerceError(count);
    if (error) {
      return error;
    } else {
      return recursiveAttemptRequest(
        `${STOREFRONT_BIG_COMMERCE_API_URL}`,
        method,
        headers,
        content,
        ++count
      );
    }
  } else {
    return response;
  }
};

const handleBigCommerceError = (count: number) => {
  if (count === 0) {
    localStorage.removeItem("bigCommerceStorefrontToken");
  }
  if (count === 1) {
    return Promise.reject({
      message:
        "Auth failed too many times, this could indicate an issue with the server's API Key.",
      error: 401,
    });
  }
  return;
};

export const fetchBigCommerceApi = (
  method: HTTP_METHOD = HTTP_METHOD.GET,
  headers: HeadersInit = {},
  content: any = undefined
): Promise<any> => {
  return recursiveAttemptRequest(
    `${STOREFRONT_BIG_COMMERCE_API_URL}`,
    method,
    headers,
    content
  );
};
